import React from 'react';
import {
  Upload,
  UploadOnAddEvent,
  ExternalDropZone
} from '@progress/kendo-react-upload';
import { Col, Row } from 'react-bootstrap';
import { Button } from '@progress/kendo-react-buttons';
import { NumericTextBox } from '@progress/kendo-react-inputs';
import { Error as ErrorLabel, Label } from '@progress/kendo-react-labels';

import {
  EventPlaylistChallenge,
  EventPlaylistChallengeSubmissionListItem
} from '../../../types/event';
import { getStatusBadge } from '.';
import { NoteInput } from './note-input';
import {
  useGetEventPlaylistChallenges,
  useGetPlaylistChallengeSubmissions
} from '../../../hooks/services/use-challenges';
import DurationPicker from '../../forms/duration-picker';
import { PostResourceAsyncWithProgress } from '../../../services/files';
import { PostPlaylistChallengeSubmission } from '../../../services/events';

const allowedFile = ['.mp4', '.mov'];
const allowedFileSize = 262144000;

const formatBytes = (bytes: number, decimals = 2) => {
  if (!+bytes) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))}${sizes[i]}`;
};

const CircularProgress = ({ percent }: { percent: number }) => (
  <div
    className={'d-flex align-items-center justify-content-center'}
    style={{
      width: 64,
      height: 64,
      borderRadius: '50%',
      background: `conic-gradient(#0d6efd ${percent * 3.6}deg, #e9ecef 0deg)`,
      transition: 'background 0.15s ease'
    }}>
    <div
      className={
        'd-flex align-items-center justify-content-center bg-white fw-semibold'
      }
      style={{ width: 52, height: 52, borderRadius: '50%', fontSize: 13 }}>
      {percent}%
    </div>
  </div>
);

export const SubmissionForm: React.FC<{
  playlistId: number | undefined;
  activityEventId: number | undefined;
  submittingChallenge: EventPlaylistChallenge | null;
  submittedSubmission: EventPlaylistChallengeSubmissionListItem | null;
  onCancel: () => void;
}> = ({
  playlistId,
  activityEventId,
  submittingChallenge,
  submittedSubmission,
  onCancel
}) => {
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [uploadProgress, setUploadProgress] = React.useState<number>(0);
  const [submissionNotes, setSubmissionNotes] = React.useState(
    submittedSubmission?.submissionNotes ?? ''
  );
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [localVideoBlobUrl, setLocalVideoBlobUrl] = React.useState<string>(
    submittedSubmission?.videoUrl ?? ''
  );

  const [timeValue, setTimeValue] = React.useState<number>(0);
  const [numberValue, setNumberValue] = React.useState<number>(0);

  const uploadRef = React.useRef<Upload>(null);

  const { mutateChallenges } = useGetEventPlaylistChallenges(
    activityEventId,
    playlistId
  );

  const { submissions, mutateSubmissions } = useGetPlaylistChallengeSubmissions(
    activityEventId,
    playlistId
  );

  const sub = submissions.find(
    (s) => s.challengeId === submittingChallenge?.challengeId
  );
  const statusInfo = getStatusBadge(sub?.status);

  const onBrowseClick = () => {
    if (uploadRef && uploadRef.current && uploadRef.current.actionElement) {
      uploadRef.current.actionElement.click();
    }
  };

  const handleDropFiles = (event: UploadOnAddEvent) => {
    setSubmitError(null);
    const fileEntry = event.newState[0];

    if (fileEntry && fileEntry.getRawFile) {
      if (fileEntry.validationErrors) {
        if (fileEntry.validationErrors.includes('invalidFileExtension')) {
          setSubmitError(
            'Invalid file type. Only MP4 and MOV files are allowed.'
          );
          return;
        }
        if (fileEntry.validationErrors.includes('invalidMaxFileSize')) {
          setSubmitError(
            `File exceeds the ${formatBytes(allowedFileSize)} limit. Please upload a smaller file.`
          );
          return;
        }
      }

      const file = fileEntry.getRawFile();
      if (file) {
        if (localVideoBlobUrl) {
          URL.revokeObjectURL(localVideoBlobUrl);
        }
        setSelectedFile(file);
        const objectUrl = URL.createObjectURL(file);
        setLocalVideoBlobUrl(objectUrl);
      }
    }
  };

  const handleSubmitAttempt = async () => {
    if (!submittingChallenge || !activityEventId || !playlistId) return;

    if (submittingChallenge.isTimeBased) {
      if (timeValue === 0) {
        setSubmitError('Completion time is required.');
        return;
      }
    } else {
      if (numberValue === null) {
        setSubmitError('Number is required.');
        return;
      }
    }

    if (!selectedFile) {
      setSubmitError('Video evidence is required.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    setUploadProgress(0);
    try {
      // 1. Upload file with progress tracking
      const uploadedResource = await PostResourceAsyncWithProgress(
        selectedFile,
        setUploadProgress
      );
      const uploadedVideoUrl = uploadedResource.value;

      if (!uploadedVideoUrl) {
        throw new Error('Failed to retrieve uploaded video URL.');
      }

      // 2. Submit the challenge attempt
      await PostPlaylistChallengeSubmission(activityEventId, playlistId, {
        challengeId: submittingChallenge.challengeId,
        videoUrl: uploadedVideoUrl,
        submissionNotes: submissionNotes || undefined,
        completionTime: submittingChallenge.isTimeBased ? timeValue : null,
        number: !submittingChallenge.isTimeBased ? numberValue : null
      });

      await mutateChallenges();
      await mutateSubmissions();

      onCancel();
    } catch (err: any) {
      console.error('Failed to submit challenge attempt', err);
      setSubmitError(
        err?.message || 'Failed to submit challenge attempt. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
      setUploadProgress(0);
    }
  };

  React.useEffect(() => {
    if (!submittedSubmission || !submittingChallenge) return;

    const isOfficialScore =
      submittedSubmission.officialScore &&
      submittedSubmission.officialScore > 0;
    submittingChallenge.isTimeBased
      ? setTimeValue(
          isOfficialScore
            ? (submittedSubmission.officialScore ?? 0)
            : (submittedSubmission.completionTime ?? 0)
        )
      : setNumberValue(
          isOfficialScore
            ? (submittedSubmission.officialScore ?? 0)
            : (submittedSubmission.number ?? 0)
        );
  }, [submittedSubmission, submittingChallenge]);

  return (
    <div
      className={'card p-4 gap-4'}
      style={{
        backgroundColor: '#F8FCFE'
      }}>
      <div
        className={
          'd-flex justify-content-between align-items-center pb-3 border-bottom'
        }>
        <div className={'d-flex flex-column'}>
          <span className={'fw-bold text-primary'}>
            {submittingChallenge?.challengeName}
          </span>
          <span>{submittingChallenge?.challengeDescription}</span>
        </div>

        <span
          style={{
            padding: '4px 8px',
            borderRadius: '5px',
            fontSize: '14px',
            ...statusInfo.style
          }}>
          {statusInfo.text}
        </span>
      </div>

      <div className={'d-flex flex-column'}>
        <div className={'d-flex flex-column'}>
          <span className={'fw-bold'}>Video evidence</span>
          <span className={submittedSubmission ? 'd-none' : ''}>
            Upload a single video file that shows your attempt from start to
            finish
          </span>
        </div>

        <Row className={'m-0'}>
          <Col md={8} className={'pl-0'}>
            <div className={submittedSubmission ? 'd-none' : ''}>
              {!selectedFile ? (
                <div className={'dropzone-upload-submission'}>
                  <div className="text-center">
                    <ExternalDropZone
                      style={{
                        borderRadius: '8px'
                      }}
                      className={'w-100 text-primary p-2 bg-white'}
                      uploadRef={uploadRef}
                      customNote={`MP4 and MOV formats only. Max ${formatBytes(allowedFileSize)}.`}
                      customHint={
                        <div className={'d-flex flex-column gap-2'}>
                          <span>Drag and drop a video to upload</span>
                          <span
                            style={{
                              fontSize: '14px'
                            }}>
                            or{' '}
                            <span
                              className={'text-primary cursor-pointer'}
                              onClick={onBrowseClick}>
                              browse
                            </span>
                          </span>
                        </div>
                      }
                    />
                    <Upload
                      className={'d-none'}
                      ref={uploadRef}
                      batch={false}
                      multiple={false}
                      withCredentials={false}
                      restrictions={{
                        allowedExtensions: allowedFile,
                        maxFileSize: allowedFileSize
                      }}
                      accept={allowedFile.join(',')}
                      onAdd={handleDropFiles}
                      showFileList={false}
                      saveUrl={(files) => {
                        return new Promise((resolve) => {
                          resolve({ uid: files[0].uid });
                        });
                      }}
                    />
                  </div>
                </div>
              ) : (
                <div
                  className={
                    'position-relative d-flex align-items-center justify-content-between p-3 bg-light border rounded'
                  }>
                  <div className={'d-flex align-items-center'}>
                    <span
                      className={'material-symbols-outlined text-primary me-2'}>
                      movie
                    </span>
                    <span
                      className={'fw-semibold text-truncate'}
                      style={{ maxWidth: '400px' }}>
                      {selectedFile.name} ({formatBytes(selectedFile.size)})
                    </span>
                  </div>
                  <button
                    type={'button'}
                    className={
                      'btn btn-link text-danger p-0 d-flex align-items-center'
                    }
                    onClick={() => {
                      setSelectedFile(null);
                      if (localVideoBlobUrl) {
                        URL.revokeObjectURL(localVideoBlobUrl);
                        setLocalVideoBlobUrl('');
                      }
                    }}
                    disabled={isSubmitting}>
                    <span className={'material-symbols-outlined'}>delete</span>
                  </button>
                </div>
              )}
            </div>

            {localVideoBlobUrl && (
              <div className={'mt-3 text-center position-relative'}>
                <video
                  width={'100%'}
                  src={localVideoBlobUrl}
                  style={{ maxHeight: '300px' }}
                  controls={!isSubmitting}
                />
                {isSubmitting && (
                  <div
                    className={
                      'position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center'
                    }
                    style={{
                      background: 'rgba(0,0,0,0.55)',
                      borderRadius: '8px'
                    }}>
                    <CircularProgress percent={uploadProgress} />
                  </div>
                )}
              </div>
            )}
          </Col>
          <Col
            md={4}
            className={'d-flex flex-column flex-grow-1 pr-0 overflow-hidden'}>
            {submittingChallenge?.isTimeBased ? (
              <div className="d-flex flex-column gap-2 mb-3">
                <Label style={{ fontSize: '12px' }}>Completion time</Label>
                <DurationPicker
                  value={timeValue ?? 0}
                  onChange={(e) => setTimeValue(e)}
                  disabled={isSubmitting || !!submittedSubmission}
                  className={'mr-2'}
                  format="mm:ss"
                  nowButton={false}
                />
              </div>
            ) : (
              <div className="d-flex flex-column gap-2 mb-3">
                <Label style={{ fontSize: '12px' }}>Number</Label>
                <NumericTextBox
                  value={numberValue}
                  onChange={(e) => setNumberValue(e.value ?? 0)}
                  min={0}
                  format="d"
                  disabled={isSubmitting || !!submittedSubmission}
                />
              </div>
            )}
            {submittedSubmission ? (
              <>
                <Label
                  style={{
                    fontSize: '12px'
                  }}>
                  Notes
                </Label>
                <div
                  style={{
                    padding: '8px',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    height: '100%',
                    maxHeight: '300px',
                    overflowX: 'auto'
                  }}>
                  {submissionNotes}
                </div>
              </>
            ) : (
              <NoteInput
                label={'Notes'}
                value={submissionNotes}
                isDisabled={isSubmitting}
                placeholder="Enter notes about this submission..."
                handleInputChange={(value) => setSubmissionNotes(value)}
              />
            )}
          </Col>
        </Row>

        {submitError && <ErrorLabel>{submitError}</ErrorLabel>}
      </div>

      {!submittedSubmission && (
        <div
          style={{
            padding: '24px',
            borderRadius: '8px',
            border: '1px solid #4698CB'
          }}>
          <span className={'fw-semibold'}>
            By submitting this video you confirm that:
          </span>
          <ul className={'px-4 m-0 mt-2'}>
            <li>
              This video accurately represents our official attempt for this
              challenge.
            </li>
            <li>
              All team members and equipment comply with the challenge rules.
            </li>
          </ul>
        </div>
      )}

      <div className={'d-flex gap-2 justify-content-end'}>
        <Button
          themeColor={'secondary'}
          fillMode={'outline'}
          onClick={onCancel}
          disabled={isSubmitting}>
          {!submittedSubmission ? 'Cancel' : 'Back'}
        </Button>
        {!submittedSubmission && (
          <Button
            themeColor={'primary'}
            onClick={handleSubmitAttempt}
            disabled={isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Submit attempt'}
          </Button>
        )}
      </div>
    </div>
  );
};
